import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, Shield, Save, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const AdminPermissionsPage = () => {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.get('/admin/permissions');
      setData(res.data);
      setEdits(JSON.parse(JSON.stringify(res.data.permissions)));
    } catch { toast.error('فشل تحميل الصلاحيات'); }
    finally { setLoading(false); }
  };

  const togglePerm = (role, perm) => {
    setEdits(prev => {
      const current = prev[role] || [];
      const updated = current.includes(perm) ? current.filter(p => p !== perm) : [...current, perm];
      return { ...prev, [role]: updated };
    });
  };

  const saveRole = async (role) => {
    setSaving(role);
    try {
      await api.put(`/admin/permissions/${role}`, { permissions: edits[role] || [] });
      toast.success(`تم حفظ صلاحيات ${role}`);
    } catch { toast.error('فشل الحفظ'); }
    finally { setSaving(null); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!data) return null;

  const roles = ["عضو إداري", "محرر"];

  return (
    <div className="space-y-8 p-4 sm:p-6 max-w-4xl mx-auto" data-testid="permissions-page">
      <div>
        <h1 className="text-4xl font-bold text-accent">إدارة الصلاحيات</h1>
        <p className="text-muted-foreground text-lg mt-1">تعيين وإلغاء صلاحيات الأدوار الإدارية</p>
      </div>

      {roles.map(role => (
        <Card key={role} className="p-6 space-y-4" data-testid={`role-${role}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-indigo-100">
                <Shield className="w-6 h-6 text-indigo-700" />
              </div>
              <h2 className="text-xl font-bold text-accent">{role}</h2>
            </div>
            <Button onClick={() => saveRole(role)} disabled={saving === role} className="bg-primary hover:bg-primary/90" data-testid={`save-${role}`}>
              {saving === role ? <Loader2 className="w-4 h-4 animate-spin ml-1" /> : <Save className="w-4 h-4 ml-1" />}
              حفظ
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {data.all_permissions.map(perm => {
              const isActive = (edits[role] || []).includes(perm);
              return (
                <button
                  key={perm}
                  onClick={() => togglePerm(role, perm)}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-right ${isActive ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50 opacity-60'}`}
                  data-testid={`perm-${role}-${perm}`}
                >
                  {isActive ? <Check className="w-5 h-5 text-green-600 shrink-0" /> : <X className="w-5 h-5 text-gray-400 shrink-0" />}
                  <span className={`text-sm font-medium ${isActive ? 'text-green-800' : 'text-gray-500'}`}>{data.labels[perm]}</span>
                </button>
              );
            })}
          </div>
        </Card>
      ))}
    </div>
  );
};

export default AdminPermissionsPage;
